import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useWidgetRetryFocus, type WidgetFetchStatus } from '@/lib/useWidgetRetryFocus';
import { formatNumber } from '@/lib/format';
import { getScopedCardTitle, type StatCardWidgetDef } from '@/lib/dashboardWidgets';
import type { DashboardStatCard, ResolvedPeriod } from '@/types/shared';
import './StatCardWidget.css';

interface StatCardWidgetProps {
  widget: StatCardWidgetDef;
  /** `null` while loading/error — resolved from the shared `/dashboard/summary` group fetch. */
  card: DashboardStatCard | null;
  /** All 5 stat cards share one `/summary` fetch, so they error/retry as a group (T81 Decision D)
   * — this is the group's status, not per-card. */
  status: WidgetFetchStatus;
  onRetry: () => void;
  /**
   * T97/T98 — the server-echoed window, present only when the caller explicitly picked a period.
   * `null` in the default (unfiltered) view. Only affects `widget.scoped` cards' titles; the 3
   * "live" cards ignore it and render the "as of now" marker instead.
   */
  resolvedPeriod: ResolvedPeriod | null;
}

/**
 * One Admin stat-card widget (`11-dashboard-widget-grid-ux.md §2`). Loading = shape-matched
 * skeleton (not a spinner, no tab stop). Loaded (incl. `0`) = the whole card is a single `Link`
 * to `card.deepLink`; the accessible name is the natural concatenation of glyph(hidden) -> title
 * -> number -> "view all" text (§2.2). Error = title stays visible, number area replaced by an
 * inline `role="status"` error + a single-tab-stop retry button.
 */
export function StatCardWidget({
  widget,
  card,
  status,
  onRetry,
  resolvedPeriod,
}: StatCardWidgetProps) {
  const { t, i18n } = useTranslation();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const retryButtonRef = useRef<HTMLButtonElement>(null);
  const retriedRef = useRef(false);
  const headingId = `dashboard-stat-heading-${widget.key}`;

  useWidgetRetryFocus(status, retriedRef, headingRef, retryButtonRef);

  function handleRetryClick() {
    retriedRef.current = true;
    onRetry();
  }

  const title = widget.scoped
    ? getScopedCardTitle(widget, resolvedPeriod, t, i18n.language)
    : t(widget.titleKey);
  const viewAllLabel = t(widget.viewAllKey);
  const retryAriaLabel = t('dashboard.widgets.common.retryAriaLabelTemplate', {
    widgetTitle: title,
  });
  const formattedValue = card ? formatNumber(card.value, i18n.language) : null;
  const Icon = widget.icon;
  const iconChip = (
    <span
      className={`fa-stat-card__icon-chip fa-stat-card__icon-chip--${widget.accent}`}
      aria-hidden="true"
    >
      <Icon />
    </span>
  );
  // T97 §"3 live cards never change with the filter, persistent 'as of now' marker in every
  // state" — the filter never touches these 3 cards' counts, so the marker renders alongside the
  // title regardless of status (skeleton/loaded/error), for every `!widget.scoped` card.
  const asOfNowMarker = !widget.scoped && (
    <span className="fa-stat-card__as-of-now">{t('dashboard.widgets.common.asOfNowLabel')}</span>
  );

  return (
    <section className="fa-stat-card" aria-labelledby={headingId} aria-busy={status === 'loading'}>
      {status === 'loading' && (
        // Title/icon are frontend-owned and already known, so only the number (the one thing
        // actually coming from the fetch) is shimmer-placeholdered — the heading stays real,
        // visible text so a screen-reader user landing on the section while busy still hears it
        // (§5 "screen-reader users landing on the section while it's busy hear the heading
        // only"). `aria-label` here is a static (non-live) name for the skeleton sub-region only
        // — not an announcement — so it's read if explored, never auto-announced. The accent icon
        // chip renders at full opacity even while loading (13-dashboard-restyle-spec.md §1.4) —
        // it's frontend-owned, not fetched, so there's nothing to placeholder.
        <div
          className="fa-stat-card__skeleton"
          aria-label={t('dashboard.widgets.common.loadingAriaLabel')}
        >
          {iconChip}
          <h3 id={headingId} ref={headingRef} tabIndex={-1} className="fa-stat-card__title">
            {title}
          </h3>
          {asOfNowMarker}
          <span
            className="fa-stat-card__skeleton-line fa-stat-card__skeleton-line--value"
            aria-hidden="true"
          />
        </div>
      )}

      {status === 'error' && (
        <div className="fa-stat-card__body">
          {iconChip}
          <h3 id={headingId} ref={headingRef} tabIndex={-1} className="fa-stat-card__title">
            {title}
          </h3>
          {asOfNowMarker}
          <div className="fa-stat-card__error" role="status">
            <p>{t('dashboard.widgets.common.errorMessage')}</p>
            <button
              type="button"
              ref={retryButtonRef}
              className="fa-stat-card__retry-btn"
              onClick={handleRetryClick}
              aria-label={retryAriaLabel}
            >
              {t('dashboard.widgets.common.retryLabel')}
            </button>
          </div>
        </div>
      )}

      {status === 'loaded' && card && (
        <Link to={card.deepLink} className="fa-stat-card__link">
          {iconChip}
          <h3 id={headingId} ref={headingRef} tabIndex={-1} className="fa-stat-card__title">
            {title}
          </h3>
          {asOfNowMarker}
          <span className="fa-stat-card__value fa-ltr-isolate">{formattedValue}</span>
          <span className="fa-stat-card__view-all">
            {viewAllLabel}
            <span className="fa-stat-card__chevron" aria-hidden="true" />
          </span>
        </Link>
      )}
    </section>
  );
}
